import { ReactNode } from 'react'

import clsx from 'clsx'

import {
  AnalysisExecStatus,
  CompanySignal,
  CompetitorSignal,
  MarketSignal
} from '@/common/api/params/mediaDriver'
import { dashboardPagePath } from '@/common/app/modules/media/mediaAnalysis'
import { mediaAnalysisHistoryPath, mediaAnalysisPath } from '@/common/app/path'
import { dateTimeStr } from '@/common/form/date'

import Badge, { BadgeType } from '@/components/view/atoms/badge/Badge'
import Button from '@/components/view/atoms/button/Button'
import Heading from '@/components/view/atoms/heading/Heading'
import SummaryHeading from '@/components/view/atoms/heading/SummaryHeading'
import Icon, { IconColor, IconType } from '@/components/view/atoms/icon/Icon'
import SummaryItem from '@/components/view/atoms/summary/SummaryItem'
import SummaryItemList from '@/components/view/atoms/summary/SummaryItemList'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import ItemAlign from '@/components/view/layouts/item/ItemAlign'

// 共通のステータスカード枠
const StatusCard = ({
  label,
  headline,
  hasData,
  lastUpdated,
  extra,
  children
}: {
  label: string
  headline: string
  hasData: boolean
  lastUpdated?: string | null
  extra?: { label: string; value: string; unit: string }[]
  children?: ReactNode
}) => (
  <Area bottomMargin={0}>
    <Heading subText={lastUpdated && <Text size={12}>最終更新: {dateTimeStr(lastUpdated)}</Text>}>
      {label}
    </Heading>
    <AreaContent>
      {extra && (
        <SummaryItemList>
          {extra.map((e, i) => (
            <SummaryItem key={i}>
              <Text weight='bold' size={12}>
                {e.label}
              </Text>
              <ItemAlign>
                <Text weight='bold' size={20}>
                  {e.value}
                </Text>
                <Text size={14}>{e.unit}</Text>
              </ItemAlign>
            </SummaryItem>
          ))}
        </SummaryItemList>
      )}
      {children}
    </AreaContent>
  </Area>
)

// 分析の実施状況リスト（実施済みチェック + 未実施は実行ボタン）
const ExecStatusList = ({
  siteId,
  title,
  items,
  metricOf
}: {
  siteId: number
  title: string
  items: AnalysisExecStatus[]
  metricOf?: (item: AnalysisExecStatus) => string | null
}) => {
  if (items.length === 0) return null
  return (
    <div className='flex flex-col gap-1.5'>
      <Text size={10} weight='bold'>
        {title}
      </Text>
      <div className='flex flex-col gap-1'>
        {items.map((a) => {
          const metric = metricOf?.(a)
          return (
            <div
              key={a.key}
              className={clsx(
                'flex items-center justify-between gap-2 rounded-lg px-4 py-2',
                a.executed ? 'bg-success' : 'bg-fill-secondary'
              )}
            >
              <div className='flex flex-wrap items-center gap-1.5'>
                <Icon
                  type={IconType.CIRCLE_CHECK}
                  color={a.executed ? IconColor.SUCCESS : IconColor.SECONDARY}
                />
                {a.label && (
                  <Text
                    size={12}
                    color={a.executed ? TextColor.PRIMARY : TextColor.SECONDARY}
                    weight={'bold'}
                  >
                    {a.label}
                  </Text>
                )}
                {a.executed && a.recent_30d_count > 0 && (
                  <Text size={10}>（直近30日 {a.recent_30d_count}回実行）</Text>
                )}
                {a.executed && metric && (
                  <Text size={10} color={TextColor.SUCCESS}>
                    {metric}
                  </Text>
                )}
              </div>
              {!a.executed && <Button link={mediaAnalysisPath(siteId)}>今すぐ実行</Button>}
            </div>
          )
        })}
      </div>
    </div>
  )
}

interface Props {
  siteId: number
  market: MarketSignal
  competitor: CompetitorSignal
  company: CompanySignal
}

// 現在のコンテンツ状況（市場・競合・自社の3カード）
const MediaStatusCards = ({ siteId, market, competitor, company }: Props) => {
  return (
    <>
      <SummaryHeading>現在のコンテンツ状況</SummaryHeading>
      <div className='mb-4 grid grid-cols-1 gap-4 lg:grid-cols-3'>
        {/* 市場の状況 */}
        <StatusCard
          label='市場の状況'
          headline={market.headline}
          hasData={market.has_data}
          lastUpdated={market.last_updated}
        >
          {(market.top_keywords ?? []).length > 0 && (
            <div className='flex flex-col gap-1'>
              <Text size={12} weight='bold'>
                直近30日で注目度が高いキーワード
              </Text>
              <div className='border-secondary rounded-lg border-2'>
                {market.top_keywords.slice(0, 3).map((kw, index) => (
                  <div
                    key={index}
                    className='border-secondary flex items-center gap-2 p-2 not-last:border-b'
                  >
                    <Badge badgeType={BadgeType.SQUARE_CAUTION}>
                      <ItemAlign gapX={2} align='center' direction='horizon'>
                        <Icon type={IconType.CROWN} color={IconColor.CAUTION} />
                        <Text color={TextColor.CAUTION} weight={'bold'}>
                          {index + 1}
                        </Text>
                      </ItemAlign>
                    </Badge>
                    <Badge size={10} badgeType={BadgeType.KEYWORD}>
                      {kw.keyword}
                    </Badge>
                    <div className='ml-auto'>
                      <Text size={12}>
                        {kw.monthly_volume ? `${kw.monthly_volume.toLocaleString()}/月` : '-'}
                      </Text>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </StatusCard>

        {/* 競合の状況 */}
        <StatusCard
          label='競合の状況'
          headline={competitor.headline}
          hasData={competitor.has_data}
          lastUpdated={competitor.last_updated}
          extra={[
            {
              label: '分析済み競合',
              value: `${competitor.competitor_count || 0}`,
              unit: '社'
            },
            { label: '検出ギャップ', value: `${competitor.total_gaps || 0}`, unit: '' },
            {
              label: '実施済み分析',
              value: `${competitor.executed_count || 0}`,
              unit: `/ ${competitor.total_count || 1}種類`
            }
          ]}
        >
          <ExecStatusList
            siteId={siteId}
            title='分析の実施状況'
            items={competitor.analyses_status ?? []}
          />
        </StatusCard>

        {/* 自社の状況 */}
        <StatusCard
          label='自社の状況'
          headline={company.headline}
          hasData={company.has_data}
          lastUpdated={company.last_updated}
          extra={[
            { label: 'リライト累計', value: `${company.total_article_versions}`, unit: '版' },
            { label: '分析累計', value: `${company.total_analyses}`, unit: '件' },
            { label: '週平均更新', value: `${company.avg_weekly_rewrites}`, unit: '件' }
          ]}
        >
          <ExecStatusList
            siteId={siteId}
            title='自社内分析の実施状況'
            items={company.self_analyses ?? []}
            metricOf={(a) => (a as { metric_text?: string | null }).metric_text ?? null}
          />
        </StatusCard>
      </div>
    </>
  )
}

export default MediaStatusCards
